import { memo } from "react";
import type { StrainGraph, GraphNode, GraphEdge } from "@/types";
import { nodeMeta, edgeMeta } from "@/lib/vocabulary";

// a lightweight static svg preview of a strain graph for feed cards.
// not interactive — just enough to signal the shape & density of the idea.
export const MiniGraph = memo(function MiniGraph({
  graph, width = 280, height = 130, showLabels = false,
}: {
  graph: StrainGraph; width?: number; height?: number; showLabels?: boolean;
}) {
  // docked modifiers live on their anchor node, not in the skeleton preview
  const docked = new Set(graph.nodes.filter((n) => n.attachment).map((n) => n.id));
  const nodes = graph.nodes.filter((n) => !docked.has(n.id));
  const edges = graph.edges.filter((e) => !docked.has(e.source) && (!e.target || !docked.has(e.target)));
  if (nodes.length === 0) return <svg width={width} height={height} />;

  // normalize positions into the viewport
  const xs = nodes.map((n) => n.position.x);
  const ys = nodes.map((n) => n.position.y);
  const minX = Math.min(...xs), maxX = Math.max(...xs);
  const minY = Math.min(...ys), maxY = Math.max(...ys);
  const spanX = Math.max(1, maxX - minX);
  const spanY = Math.max(1, maxY - minY);
  const pad = 14;
  const scale = Math.min((width - pad * 2) / spanX, (height - pad * 2) / spanY, 0.4);

  const project = (n: GraphNode) => ({
    x: pad + (n.position.x - minX) * scale,
    y: pad + (n.position.y - minY) * scale,
  });

  const nodeMap = new Map(nodes.map((n) => [n.id, n]));
  const pts = new Map(nodes.map((n) => [n.id, project(n)]));

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="block">
      <defs>
        {edges.map((e) => {
          const meta = edgeMeta(e.relation);
          return (
            <marker
              key={e.id}
              id={`mk-${e.id}`}
              markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto"
            >
              <path d="M0,0 L6,3 L0,6 Z" fill={meta.color} opacity="0.7" />
            </marker>
          );
        })}
      </defs>

      {/* edges */}
      {edges.map((e) => {
        const from = nodeMap.get(e.source);
        const to = e.target ? nodeMap.get(e.target) : null;
        const targetEdge = e.target_edge ? edges.find((x) => x.id === e.target_edge) : null;
        if (!from) return null;
        const a = pts.get(from.id)!;
        let b: { x: number; y: number };
        if (to) b = pts.get(to.id)!;
        else if (targetEdge) {
          const ts = nodeMap.get(targetEdge.source);
          const tt = targetEdge.target ? nodeMap.get(targetEdge.target) : null;
          if (!ts || !tt) return null;
          const p1 = pts.get(ts.id)!, p2 = pts.get(tt.id)!;
          b = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
        } else return null;

        const meta = edgeMeta(e.relation);
        const dash = meta.stroke === "dashed" ? "4 3" : meta.stroke === "dotted" ? "1.5 3" : undefined;
        const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2;
        return (
          <g key={e.id}>
            <path
              d={`M${a.x},${a.y} Q${mx},${my - 6} ${b.x},${b.y}`}
              stroke={meta.color}
              strokeWidth={e.relation === "undercuts" ? 1.4 : 1.2}
              fill="none"
              strokeDasharray={dash}
              opacity={0.55}
              markerEnd={`url(#mk-${e.id})`}
            />
          </g>
        );
      })}

      {/* nodes */}
      {nodes.map((n) => {
        const p = pts.get(n.id)!;
        const meta = nodeMeta(n.type);
        const isThesis = n.badges?.includes("thesis");
        const isCrux = n.badges?.includes("crux") || !!n.crux;
        const r = isThesis ? 5 : isCrux ? 4 : 3.2;
        return (
          <g key={n.id}>
            {isThesis && (
              <circle cx={p.x} cy={p.y} r={r + 3} fill="none" stroke={meta.color} strokeWidth="0.8" opacity="0.5" />
            )}
            <circle
              cx={p.x} cy={p.y} r={r}
              fill={n.provenance === "ai_inferred" ? "var(--color-surface)" : meta.color}
              stroke={meta.color}
              strokeWidth="1.2"
            />
            {n.provenance === "ai_inferred" && (
              <circle cx={p.x} cy={p.y} r={r - 1.5} fill="var(--color-inferred)" />
            )}
          </g>
        );
      })}
    </svg>
  );
});
